/**
 * Prenos hrán medzi dielcami — schránka a typ hrany na stranách (KOLO 177).
 *
 * User: čierna doska s čiernou detegovanou hranou a vedľa čierna doska
 * s dekorovou hranou — hrany chce meniť naraz, nie dielec po dielci cez
 * detegované hrany materiálu. Dve cesty, jedno pravidlo:
 *
 * - **Kopírovať / Vložiť hrany** — schránka si zapamätá strany a hrany
 *   zdrojového dielca; cieľ dostane tie isté strany a typ hrany v šírke
 *   pre **svoju** dosku (`hranaPodlaVzoru`, rodinu aj šírku rozhoduje
 *   backend — 18 mm zdroj, 36 mm cieľ dostane 42 mm pásku).
 * - **Typ hrany na existujúcich stranách** — hromadná úprava bez vybraných
 *   strán zmení typ len tam, kde hrana je, a hrúbka každej strany ostane.
 *
 * Schránka je stav stránky (nie systémová schránka) — drží id hrán, nie
 * text, a prežije prechod medzi dielcami aj zákazkami.
 */
import { useSyncExternalStore } from "react";
import { api, extractErrorMessage } from "@/api/client";
import type { DataResponse, Dielec, Material } from "@/api/schemas";
import { hranaPodlaVzoru, STRANY_HRAN } from "@/lib/autoEdge";
import type { HranyState } from "@/components/dielec/HranyEditor";

export type StranaHrany = (typeof STRANY_HRAN)[number];

/** Strana zdroja v schránke — hrana, auto, alebo bez hrany. */
export interface StranaSchranky {
  id: string | null;
  auto: boolean;
}

export interface SchrankaHran {
  dielecId: string;
  kod: string | null;
  strany: Record<StranaHrany, StranaSchranky>;
}

let schranka: SchrankaHran | null = null;
const posluchaci = new Set<() => void>();

export function nastavSchrankuHran(nova: SchrankaHran | null): void {
  schranka = nova;
  for (const f of posluchaci) f();
}

function prihlas(f: () => void): () => void {
  posluchaci.add(f);
  return () => posluchaci.delete(f);
}

/** Aktuálna schránka hrán — prekreslí sa, keď ju niekto zmení. */
export function useSchrankaHran(): SchrankaHran | null {
  return useSyncExternalStore(prihlas, () => schranka);
}

/** Schránka z hrán dielca v detaile (lokálny stav panelu). */
export function schrankaZHran(
  dielecId: string,
  kod: string | null,
  hrany: HranyState,
): SchrankaHran {
  const strany = {} as Record<StranaHrany, StranaSchranky>;
  for (const s of STRANY_HRAN) strany[s] = { id: hrany[s].id, auto: hrany[s].auto };
  return { dielecId, kod, strany };
}

/** Hrúbka hrany z cenníka — pre typ hrany v rovnakej hrúbke. */
async function hrubkaHrany(
  id: string,
  cache: Map<string, Promise<string | null>>,
): Promise<string | null> {
  let p = cache.get(id);
  if (!p) {
    p = api
      .get<DataResponse<Material>>(`/cennik/materialy/${id}`)
      .then(({ data }) =>
        data.data?.hrubka_mm != null ? String(data.data.hrubka_mm) : null,
      );
    cache.set(id, p);
  }
  return p;
}

/** Spoločné kešky jednej akcie — dvadsať 18 mm dielcov je jedna otázka. */
export interface KeskyPrenosu {
  hrubky: Map<string, Promise<string | null>>;
  vzory: Map<string, ReturnType<typeof hranaPodlaVzoru>>;
}

export function noveKesky(): KeskyPrenosu {
  return { hrubky: new Map(), vzory: new Map() };
}

async function typVHrubke(
  vzorId: string,
  zMm: number | null,
  hrubka: string | null,
  k: KeskyPrenosu,
): Promise<{ id: string } | { chyba: string }> {
  const kluc = `${vzorId}|${zMm ?? ""}|${hrubka ?? ""}`;
  let dopyt = k.vzory.get(kluc);
  if (!dopyt) {
    dopyt = hranaPodlaVzoru(vzorId, zMm, hrubka ?? undefined);
    k.vzory.set(kluc, dopyt);
  }
  const r = await dopyt;
  if (!r.edge) return { chyba: r.sprava ?? "typ hrany sa v potrebnej šírke nevyrába" };
  return { id: r.edge.id };
}

export type VysledokHran =
  | { hrany: HranyState }
  | { chyba: string };

/**
 * Hrany zo schránky pre cieľ s doskou ``zMm`` — tie isté strany, typ
 * v šírke cieľa a v hrúbke zdroja. Auto strana ostane auto.
 */
export async function hranyZoSchranky(
  zdroj: SchrankaHran,
  zMm: number | null,
  k: KeskyPrenosu = noveKesky(),
): Promise<VysledokHran> {
  const hrany = {} as HranyState;
  for (const s of STRANY_HRAN) {
    const z = zdroj.strany[s];
    if (!z.id) {
      hrany[s] = { id: null, auto: z.auto };
      continue;
    }
    const hrubka = await hrubkaHrany(z.id, k.hrubky);
    const r = await typVHrubke(z.id, zMm, hrubka, k);
    if ("chyba" in r) return { chyba: `${s.toUpperCase()}: ${r.chyba}` };
    hrany[s] = { id: r.id, auto: false };
  }
  return { hrany };
}

/**
 * Typ ``vzorId`` na stranách, ktoré hranu majú — hrúbka každej strany
 * ostane, strany bez hrany sa nezapnú. ``null`` = dielec nemá žiadnu
 * hranu, nie je čo meniť.
 */
export async function typNaExistujucichStranach(
  d: Dielec,
  vzorId: string,
  zMm: number | null,
  k: KeskyPrenosu = noveKesky(),
): Promise<{ data: Record<string, unknown> } | { chyba: string } | null> {
  const data: Record<string, unknown> = {};
  for (const s of STRANY_HRAN) {
    const id = d[`hrana_${s}_id`];
    if (!id) continue;
    const hrubka = await hrubkaHrany(id, k.hrubky);
    const r = await typVHrubke(vzorId, zMm, hrubka, k);
    if ("chyba" in r) return { chyba: `${s.toUpperCase()}: ${r.chyba}` };
    data[`hrana_${s}_id`] = r.id;
    data[`hrana_${s}_auto`] = false;
  }
  return Object.keys(data).length > 0 ? { data } : null;
}

/** Hrany ako polia PATCH-u dielca. */
export function hranyDoUpravy(hrany: HranyState): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const s of STRANY_HRAN) {
    data[`hrana_${s}_id`] = hrany[s].id;
    data[`hrana_${s}_auto`] = hrany[s].auto;
  }
  return data;
}

/** Výsledok vloženia hrán na viac dielcov naraz. */
export interface VysledokVlozeniaHran {
  /** Dielce, ktorým sa hrany poslali na uloženie. */
  ulozene: Dielec[];
  chyby: { id: string; sprava: string }[];
  /** Masív, cenovka — hrany nedostanú (`bezStran`). */
  preskocene: Dielec[];
}

/**
 * Hrany zo schránky na všetky ``dielce`` — hromadná úprava aj menu riadku
 * (KOLO 181) idú cez toto jedno miesto. Typ hrany sa hľadá v šírke
 * každého dielca (`hranyZoSchranky`), ukladá volajúci (`uloz`).
 */
export async function vlozHranyNaDielce(
  dielce: Dielec[],
  zdroj: SchrankaHran,
  {
    bezStran,
    uloz,
  }: {
    bezStran: (d: Dielec) => boolean;
    uloz: (d: Dielec, data: Record<string, unknown>) => Promise<void>;
  },
): Promise<VysledokVlozeniaHran> {
  const vysledok: VysledokVlozeniaHran = { ulozene: [], chyby: [], preskocene: [] };
  const k = noveKesky();
  for (const d of dielce) {
    if (bezStran(d)) {
      vysledok.preskocene.push(d);
      continue;
    }
    let r: VysledokHran;
    try {
      const zMm = d.z_mm != null ? parseFloat(String(d.z_mm)) : null;
      r = await hranyZoSchranky(zdroj, zMm, k);
    } catch (err) {
      r = { chyba: extractErrorMessage(err) };
    }
    if ("chyba" in r) {
      vysledok.chyby.push({ id: d.id, sprava: r.chyba });
      continue;
    }
    await uloz(d, hranyDoUpravy(r.hrany));
    vysledok.ulozene.push(d);
  }
  return vysledok;
}

/** Schránka z hrán uloženého dielca — Kopírovať hrany v menu riadku. */
export function schrankaZDielca(d: Dielec): SchrankaHran {
  const strany = {} as Record<StranaHrany, StranaSchranky>;
  for (const s of STRANY_HRAN) {
    strany[s] = { id: d[`hrana_${s}_id`] ?? null, auto: d[`hrana_${s}_auto`] ?? false };
  }
  return { dielecId: d.id, kod: d.kod ?? null, strany };
}
